import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { FormRow } from "@/components/admin/form";
import { useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { fmtFCFA } from "@/lib/format";
import type { Order } from "@/lib/orders/types";

/**
 * Motifs d'annulation.
 *
 * L'ancienne fenêtre système demandait un motif libre : chacun écrivait ce
 * qu'il voulait, si bien qu'aucune statistique d'annulation n'était possible —
 * alors que c'est précisément le chiffre qui dit ce qui ne va pas dans la
 * boutique. Une liste courte, tirée des cas réellement rencontrés ici, se
 * saisit plus vite qu'une phrase et se compte.
 */
const MOTIFS = [
	{ code: "CLIENT_RENONCE", label: "Le client s'est désisté" },
	{ code: "CLIENT_INJOIGNABLE", label: "Client injoignable" },
	{ code: "RUPTURE_STOCK", label: "Produit finalement indisponible" },
	{ code: "ERREUR_SAISIE", label: "Erreur de saisie" },
	{ code: "DOUBLON", label: "Commande en double" },
	{ code: "HORS_ZONE", label: "Adresse hors zone de livraison" },
	{ code: "SUSPICION_FRAUDE", label: "Suspicion de fraude" },
	{ code: "TEST_INTERNE", label: "Commande de test interne" },
] as const;

interface Props {
	order: Order;
	onClose: () => void;
	onConfirm: (reasonCode: string) => Promise<void>;
}

/**
 * Annulation d'une commande.
 *
 * Remplace un `window.prompt` : au-delà de l'aspect, la fenêtre système ne
 * permettait d'afficher ni le montant en jeu, ni l'avertissement sur la
 * validation par un second responsable — l'opérateur découvrait le refus
 * après coup, sans comprendre pourquoi rien ne s'était passé.
 */
export function CancelOrderModal({ order, onClose, onConfirm }: Props) {
	const toast = useToast();
	const [motif, setMotif] = useState<string>("");
	const [envoi, setEnvoi] = useState(false);
	const [erreur, setErreur] = useState<string | null>(null);

	// Le serveur exige une seconde validation au-delà de ce seuil : autant le
	// dire avant, plutôt que de laisser l'opérateur croire à un échec.
	const approbationRequise = order.totalAmount >= 50_000;

	const valider = async () => {
		if (!motif) {
			setErreur("Choisissez un motif d'annulation.");
			return;
		}
		setErreur(null);
		setEnvoi(true);
		try {
			await onConfirm(motif);
			onClose();
		} catch (e) {
			const err = e as {
				response?: {
					status?: number;
					data?: { message?: string; data?: { message?: string } };
				};
			};
			// Le message arrive à plat sur la réponse d'erreur. Le chercher un
			// niveau trop bas faisait afficher « Annulation impossible » là où le
			// serveur disait simplement qu'une approbation était en attente.
			const message =
				err.response?.data?.message ?? err.response?.data?.data?.message;
			if (err.response?.status === 409 && message === "PENDING_APPROVAL") {
				toast.info(
					"Demande envoyée. Un second responsable doit la valider dans Administration › Approbations.",
				);
				onClose();
				return;
			}
			setErreur(extractErrorMessage(e, "Annulation impossible"));
		} finally {
			setEnvoi(false);
		}
	};

	return (
		<Modal
			title="Annuler la commande"
			subtitle={
				<>
					<span className="mono">{order.number}</span> ·{" "}
					<strong>{fmtFCFA(order.totalAmount)}</strong>
				</>
			}
			onClose={onClose}
			width={460}
		>
			<FormRow label="Motif" required>
				<select
					className="aform-i"
					value={motif}
					onChange={(e) => setMotif(e.target.value)}
					data-testid="cancel-reason"
				>
					<option value="">— Choisir un motif —</option>
					{MOTIFS.map((m) => (
						<option key={m.code} value={m.code}>
							{m.label}
						</option>
					))}
				</select>
			</FormRow>

			{approbationRequise && (
				<div
					style={{
						marginTop: -4,
						marginBottom: 14,
						padding: "9px 12px",
						borderRadius: 6,
						background: "var(--a-surface-2)",
						fontSize: 12.5,
						lineHeight: 1.5,
						color: "var(--a-ink-mute)",
					}}
				>
					Au-delà de 50 000 F, l'annulation demande la validation d'un second
					responsable. Votre demande partira en attente d'approbation.
				</div>
			)}

			{erreur && (
				<div
					role="alert"
					style={{
						marginBottom: 14,
						fontSize: 12.5,
						color: "var(--a-danger, #b3261e)",
					}}
				>
					{erreur}
				</div>
			)}

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose} disabled={envoi}>
					Revenir
				</AdminButton>
				<AdminButton
					variant="primary"
					onClick={valider}
					disabled={envoi || !motif}
					data-testid="cancel-confirm"
					// Même code couleur que les autres actions destructrices du
					// back-office (cf. la confirmation « tapez le nom »).
					style={{
						background: "var(--a-danger)",
						color: "#fff",
						opacity: envoi || !motif ? 0.5 : 1,
					}}
				>
					{envoi ? "Annulation…" : "Annuler la commande"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
